/**
 * RS / Marquee Menu — CSS animation base + manual scroll layer.
 *
 * Auto-scroll is a pure CSS animation (translate3d keyframes over two
 * identical halves); this element measures (clones to fill the viewport,
 * converts px/s into a duration) and toggles `is-visible` via
 * IntersectionObserver.
 *
 * Manual layer: drag and horizontal wheel scrub the track with the shared
 * hero physics (1:1 under the finger, decaying fling on release, a new touch
 * catches the glide). Interaction freezes the CSS animation at its current
 * position (computed transform) — no rAF loop exists while idle — and a few
 * seconds after the gesture settles the animation resumes from exactly where
 * the track sits, via a negative animation-delay: no jump.
 */
import { createDragPhysics } from 'rs-drag-physics';

const RESUME_DELAY_MS = 3000;

class RsMarqueeMenu extends HTMLElement {
  #observer = null;
  #manual = false;
  #resumeTimer = null;

  connectedCallback() {
    this.rail = this.querySelector('[data-rs-rail]');
    this.track = this.querySelector('[data-rs-track]');
    this.group = this.querySelector('[data-rs-group]');
    if (!this.rail || !this.track || !this.group) return;

    this.speed = parseFloat(this.dataset.speed) || 36;
    this.reverse = this.classList.contains('rs-marquee--right');
    this.offset = 0;
    this.halfWidth = 0;
    this.duration = 60;

    this.#measure();
    // Cold devices: webfonts land after connect and change the group width
    document.fonts?.ready.then(() => this.#measure());

    // iOS toolbar show/hide fires height-only resizes while the page scrolls:
    // a full re-clone would restart the CSS animation with a visible hitch
    this.lastMeasuredWidth = window.innerWidth;
    this.onResize = () => {
      if (window.innerWidth === this.lastMeasuredWidth) return;
      this.lastMeasuredWidth = window.innerWidth;
      this.#measure();
    };
    window.addEventListener('resize', this.onResize);

    this.#wire();

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.classList.toggle('is-offscreen', !entry.isIntersecting);
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.#resumeTimer);
    this.physics?.destroy();
  }

  /* Track = two identical halves; the keyframes travel -50%, so each half
     must be at least as wide as the viewport for a seamless loop. */
  #measure() {
    for (const clone of this.track.querySelectorAll('[data-rs-clone]')) clone.remove();

    const groupWidth = this.group.getBoundingClientRect().width;
    if (groupWidth === 0) return;

    const perHalf = Math.max(1, Math.ceil(window.innerWidth / groupWidth));
    const totalCopies = perHalf * 2;
    for (let i = 1; i < totalCopies; i++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('data-rs-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      // NOT inert: half the visible items are clones at any moment and they
      // must stay clickable; out of the tab order via tabindex only
      for (const focusable of clone.querySelectorAll('a, button')) focusable.tabIndex = -1;
      this.track.appendChild(clone);
    }

    this.halfWidth = groupWidth * perHalf;
    this.duration = this.halfWidth / this.speed;
    this.style.setProperty('--rs-marquee-duration', `${this.duration.toFixed(2)}s`);

    if (this.#manual) {
      this.offset = this.#wrap(this.offset);
      this.#applyOffset();
    }
  }

  #wire() {
    const rail = this.rail;

    // Shared hero physics: 1:1 drag, decaying fling, catch on touch.
    // Auto-scroll stays frozen for the whole gesture (drag + fling) and
    // resumes from the settled position via #scheduleResume
    this.physics = createDragPhysics(rail, {
      // Visual truth even while the CSS animation owns the transform
      getOffset: () => new DOMMatrixReadOnly(getComputedStyle(this.track).transform).m41,
      onDragStart: () => {
        clearTimeout(this.#resumeTimer);
        this.#enterManual();
        this.dragStartOffset = this.offset;
        rail.classList.add('is-dragging');
      },
      onMove: (dx) => {
        this.offset = this.#wrap(this.dragStartOffset - dx);
        this.#applyOffset();
      },
      onDragEnd: () => rail.classList.remove('is-dragging'),
      onFling: (delta) => {
        this.offset = this.#wrap(this.offset + delta);
        this.#applyOffset();
      },
      onSettle: () => this.#scheduleResume(),
    });

    // A completed drag must not navigate the link under the pointer
    rail.addEventListener(
      'click',
      (event) => {
        if (!this.physics.moved) return;
        this.physics.resetMoved();
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true }
    );

    // Facet items: single choice per rail. A click REPLACES every value of
    // this rail's param with the clicked one (whatever the FILTERS panel had
    // selected for it); re-clicking the only active value removes it. Every
    // other param — the other rail, size, availability, sort — is preserved.
    // The URL is the single source of truth, shared with the panel. The
    // href keeps the native cumulative URL as a no-JS fallback.
    rail.addEventListener('click', (event) => {
      const item = event.target.closest('[data-rs-facet-param]');
      if (!item || event.defaultPrevented) return;
      event.preventDefault();

      const param = item.dataset.rsFacetParam;
      const value = item.dataset.rsFacetValue;
      const url = new URL(location.href);
      const params = url.searchParams;

      const active = params.getAll(param);
      params.delete(param);
      const removing = active.length === 1 && active[0] === value;
      if (!removing) params.append(param, value);
      params.delete('page');

      // Same IN STOCK automatism as the panel (rs-filters.js), read from its
      // policy attributes (present on /collections/all only) and stateless
      // like it: only the FIRST filter applied from the clean landing (no
      // facet, no sort in the URL) adds IN STOCK. From a filtered state the
      // URL already says whether IN STOCK is on, and a manual removal holds
      // until the filters are emptied again.
      const filters = document.querySelector('rs-filters[data-rs-auto-availability]');
      if (filters) {
        const availName = filters.getAttribute('data-rs-avail-name');
        const inStock = filters.getAttribute('data-rs-avail-instock');
        const defaultSort = filters.getAttribute('data-rs-default-sort');
        const current = new URLSearchParams(location.search);
        const currentSort = current.get('sort_by');
        const clean =
          ![...current.keys()].some((key) => key.startsWith('filter.')) &&
          (currentSort === null || currentSort === defaultSort);
        const hasAvailability = params.getAll(availName).length > 0;
        const hasFacet = [...params.keys()].some((key) => key.startsWith('filter.') && key !== availName);
        if (clean && !hasAvailability && hasFacet) params.append(availName, inStock);
      }

      location.href = url.toString();
    });

    rail.addEventListener(
      'wheel',
      (event) => {
        const dx = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
        if (dx === 0) return;
        event.preventDefault();
        this.physics.stopFling();
        this.#enterManual();
        this.offset = this.#wrap(this.offset + dx);
        this.#applyOffset();
        this.#scheduleResume();
      },
      { passive: false }
    );
  }

  /* Drop the facet items of `param` whose value is not in `keep` — an
     active item always stays, so it can be removed — then re-measure so the
     loop stays seamless with the shorter group (rs-filters.js hides the
     designers with no piece in stock this way) */
  keepFacetValues(param, keep) {
    let changed = false;
    for (const item of this.group.querySelectorAll(`[data-rs-facet-param="${param}"]`)) {
      if (keep.has(item.dataset.rsFacetValue) || item.classList.contains('is-active')) continue;
      item.remove();
      changed = true;
    }
    if (changed) this.#measure();
  }

  #wrap(value) {
    if (this.halfWidth <= 0) return value;
    return ((value % this.halfWidth) + this.halfWidth) % this.halfWidth;
  }

  #applyOffset() {
    this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  }

  /* Freeze the CSS animation exactly where it visually sits */
  #enterManual() {
    if (this.#manual) return;
    const matrix = new DOMMatrixReadOnly(getComputedStyle(this.track).transform);
    this.offset = this.#wrap(-matrix.m41);
    this.track.classList.add('is-manual');
    this.#applyOffset();
    this.#manual = true;
  }

  #scheduleResume() {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => this.#resumeAuto(), RESUME_DELAY_MS);
  }

  /* Re-enter the animation at the current position via negative delay —
     no jump, the loop just keeps going from here */
  #resumeAuto() {
    if (!this.#manual || this.physics.active || this.halfWidth <= 0) return;
    const fraction = this.offset / this.halfWidth;
    const progress = this.reverse ? 1 - fraction : fraction;
    this.track.style.animationDelay = `${(-progress * this.duration).toFixed(3)}s`;
    this.track.style.transform = '';
    this.track.classList.remove('is-manual');
    this.#manual = false;
  }
}

if (!customElements.get('rs-marquee-menu')) {
  customElements.define('rs-marquee-menu', RsMarqueeMenu);
}
